import type { LunchLitelyService } from '@b2b/common/services/lunchLitely/lunchLitely.service';
import type { PendoService } from '@b2b/common/services/pendo';
import angular, { ITimeoutService } from 'angular';
import {
  buildRedemptionLimitsTrackingProperties,
  COUPON_CREATE_SAVED_EVENT,
  COUPON_EDIT_SAVED_EVENT,
  COUPON_REDEMPTION_LIMIT_FLAG,
  CouponEditSaveAction,
  CouponRedemptionLimitsSource,
  createRedemptionLimitsForm,
  describeCouponSaveError,
  formatOrderRedemptionCount,
  orderLimitBelowCountMessage,
  REDEMPTION_LIMIT_COPY,
  validateRedemptionLimits,
} from './coupon-redemption-limits';

/** The flag check must not hold the form hostage when Lunch Litely is slow or unreachable. */
export const REDEMPTION_FLAG_TIMEOUT_MS = 3000;

/**
 * AngularJS facade over `coupon-redemption-limits.ts` for the legacy JS coupon controllers, which
 * cannot import TypeScript directly. Also answers whether the `coupons_redemption_limit` flag is on
 * and sends the coupon-saved Pendo events. Callers send those events only while the flag is on.
 */
export class CouponRedemptionLimitsService {
  static $inject = ['LunchLitelyService', '$timeout', 'PendoService'];

  readonly copy = REDEMPTION_LIMIT_COPY;
  readonly createForm = createRedemptionLimitsForm;
  readonly validate = validateRedemptionLimits;
  readonly formatOrderRedemptionCount = formatOrderRedemptionCount;
  readonly describeSaveError = describeCouponSaveError;
  readonly orderLimitBelowCountMessage = orderLimitBelowCountMessage;

  constructor(
    private lunchLitelyService: LunchLitelyService,
    private $timeout: ITimeoutService,
    private pendoService: PendoService,
  ) {}

  /** Resolves false when the flag is off, the service fails, or it takes longer than the timeout. */
  isEnabled(): Promise<boolean> {
    let timer: ReturnType<ITimeoutService> | undefined;
    const timeout = new Promise<boolean>((resolve) => {
      timer = this.$timeout(() => resolve(false), REDEMPTION_FLAG_TIMEOUT_MS, false);
    });

    return Promise.race([this.lunchLitelyService.isEnabled(COUPON_REDEMPTION_LIMIT_FLAG), timeout])
      .then((enabled) => enabled === true)
      .catch(() => false)
      .then((enabled) => {
        if (timer) {
          this.$timeout.cancel(timer);
        }
        return enabled;
      });
  }

  trackCreateSaved(limits: CouponRedemptionLimitsSource): void {
    this.pendoService.track(COUPON_CREATE_SAVED_EVENT, buildRedemptionLimitsTrackingProperties(limits));
  }

  trackEditSaved(limits: CouponRedemptionLimitsSource, saveAction: CouponEditSaveAction): void {
    this.pendoService.track(COUPON_EDIT_SAVED_EVENT, {
      ...buildRedemptionLimitsTrackingProperties(limits),
      save_action: saveAction,
    });
  }
}

export const CouponRedemptionLimitsServiceModule = angular
  .module('acme.view.application.coupons.service.redemptionLimits', [
    'acme.common.service.lunchLitely',
    'acme.common.service.pendo',
  ])
  .service('CouponRedemptionLimitsService', CouponRedemptionLimitsService).name;
